import { IconCheck, IconTrash } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { ConfirmationDialog } from '../components/ui/confirmation-dialog';

export default function Install() {
  const [locationId, setLocationId] = useState('');
  const [isUninstalling, setIsUninstalling] = useState(false);
  const [uninstallMessage, setUninstallMessage] = useState('');
  const [isInGHLIframe, setIsInGHLIframe] = useState(false);
  const [showUninstallConfirm, setShowUninstallConfirm] = useState(false);

  // GHL survey form URL - update this with your actual form URL
  const UNINSTALL_SURVEY_URL = 'https://miltonecom.com/uninstall-survey'; // Replace with your GHL form URL

  useEffect(() => {
    // Check if we're in an iframe - if so, show uninstall section
    // Since this page is only accessed through GHL marketplace, being in iframe = GHL
    const inIframe = window.self !== window.parent;
    setIsInGHLIframe(inIframe);

    console.log('Iframe detection:', {
      inIframe,
      self: window.self,
      parent: window.parent,
      location: window.location.href
    });
  }, []);

  const initiateUninstall = () => {
    if (!locationId.trim()) {
      setUninstallMessage('Please enter a Location ID');
      return;
    }
    setShowUninstallConfirm(true);
  };

  const handleUninstall = async () => {
    setShowUninstallConfirm(false);
    setIsUninstalling(true);
    setUninstallMessage('');

    try {
      const response = await fetch(`/oauth/uninstall?location_id=${encodeURIComponent(locationId.trim())}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      const data = await response.json();

      if (response.ok) {
        setUninstallMessage('✅ Successfully uninstalled InboxSentinel');
        setLocationId('');
      } else {
        setUninstallMessage(`❌ Error: ${data.detail || 'Failed to uninstall'}`);
      }
    } catch (error) {
      setUninstallMessage(`❌ Error: ${error.message}`);
    } finally {
      setIsUninstalling(false);
    }
  };

  return (
    <>
      <div className="mb-16">
        <div className="text-center mb-12">
          <h1 className="text-5xl font-black mb-4 leading-tight">See the Difference</h1>
          <p className="text-lg opacity-90 mb-12 leading-relaxed">
            InboxSentinel transforms how your team manages email conversations<br />
            in your CRM, removing spam and protecting data quality.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16 items-start">
          {/* BEFORE */}
          <div className="text-center">
            <img
              src="/images/Before-Sentinel.png"
              alt="Before InboxSentinel - Inbox full of spam"
              className="w-full max-w-sm rounded-lg mb-5 shadow-lg"
            />
            <div className="text-2xl font-bold mb-2">Before</div>
            <p className="text-base opacity-85 leading-relaxed">
              Inbox cluttered with spam, phishing attempts, and low-quality leads that waste time and create risk.
            </p>
          </div>

          {/* ARROW */}
          <div className="flex items-center justify-center text-4xl opacity-60 mt-5">→</div>

          {/* SOLUTION */}
          <div className="text-center">
            <img
              src="/images/Solution.png"
              alt="InboxSentinel Solution"
              className="w-full max-w-sm rounded-lg mb-5 shadow-lg"
            />
            <div className="text-2xl font-bold mb-2">InboxSentinel</div>
            <p className="text-base opacity-85 leading-relaxed">
              Automatic filtering analyzes emails in real-time and removes threats before they clutter your CRM.
            </p>
          </div>

          {/* ARROW */}
          <div className="flex items-center justify-center text-4xl opacity-60 mt-5">→</div>

          {/* AFTER */}
          <div className="text-center">
            <img
              src="/images/After-Sentinel.png"
              alt="After InboxSentinel - Clean, organized inbox"
              className="w-full max-w-sm rounded-lg mb-5 shadow-lg"
            />
            <div className="text-2xl font-bold mb-2">After</div>
            <p className="text-base opacity-85 leading-relaxed">
              Clean inbox with only legitimate conversations, improving team productivity and data quality.
            </p>
          </div>
        </div>

        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20">
          <h2 className="text-3xl font-bold text-center mb-6">Key Benefits</h2>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 list-none p-0 m-0">
            <li className="p-4 bg-white/5 rounded-lg flex items-start gap-3">
              <IconCheck size={24} className="text-green-400 flex-shrink-0 mt-0.5" stroke={3} />
              <span><strong>Save Hours</strong> - No more manual spam deletion</span>
            </li>
            <li className="p-4 bg-white/5 rounded-lg flex items-start gap-3">
              <IconCheck size={24} className="text-green-400 flex-shrink-0 mt-0.5" stroke={3} />
              <span><strong>Protect Data</strong> - Phishing and malware never reaches your team</span>
            </li>
            <li className="p-4 bg-white/5 rounded-lg flex items-start gap-3">
              <IconCheck size={24} className="text-green-400 flex-shrink-0 mt-0.5" stroke={3} />
              <span><strong>Improve Quality</strong> - CRM stays clean and organized</span>
            </li>
            <li className="p-4 bg-white/5 rounded-lg flex items-start gap-3">
              <IconCheck size={24} className="text-green-400 flex-shrink-0 mt-0.5" stroke={3} />
              <span><strong>Zero Setup</strong> - Install and it works automatically</span>
            </li>
            <li className="p-4 bg-white/5 rounded-lg flex items-start gap-3">
              <IconCheck size={24} className="text-green-400 flex-shrink-0 mt-0.5" stroke={3} />
              <span><strong>Completely Safe</strong> - Never stores personal data or credentials</span>
            </li>
            <li className="p-4 bg-white/5 rounded-lg flex items-start gap-3">
              <IconCheck size={24} className="text-green-400 flex-shrink-0 mt-0.5" stroke={3} />
              <span><strong>Always Learning</strong> - Improves detection as it processes emails</span>
            </li>
          </ul>
        </div>

        <div className="text-center mt-12">
          <a
            href="/oauth/install"
            className="inline-block bg-gradient-primary text-white px-12 py-5 rounded-lg font-bold text-lg shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all cursor-pointer"
          >
            Install Now
          </a>
        </div>

        {/* Uninstall Section - Only show when iframed from GHL */}
        {isInGHLIframe && (
          <div className="bg-white/5 backdrop-blur-md rounded-2xl p-8 border border-white/10 mt-16">
            <h2 className="text-2xl font-bold text-center mb-4 flex items-center justify-center gap-2">
              <IconTrash size={28} />
              Uninstall InboxSentinel
            </h2>
            <p className="text-center text-sm opacity-75 mb-6">
              Need to remove InboxSentinel from a location? Enter the Location ID below.
            </p>

            <div className="max-w-md mx-auto">
              <input
                type="text"
                value={locationId}
                onChange={(e) => setLocationId(e.target.value)}
                placeholder="Enter Location ID"
                className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50 mb-4 focus:outline-none focus:border-white/40"
              />

              {/* Link to GHL Survey Form */}
              <div className="bg-blue-500/20 border border-blue-500/50 rounded-lg p-4 mb-4 text-center">
                <p className="text-sm mb-3">Before uninstalling, we'd love to hear why you're leaving.</p>
                <a
                  href={UNINSTALL_SURVEY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg font-medium transition-colors text-sm"
                >
                  📋 Take 1-Minute Survey (Optional)
                </a>
              </div>

              <button
                onClick={initiateUninstall}
                disabled={isUninstalling}
                className="w-full bg-red-600 hover:bg-red-700 disabled:bg-gray-600 text-white px-6 py-3 rounded-lg font-semibold transition-all shadow-lg hover:shadow-xl"
              >
                {isUninstalling ? 'Uninstalling...' : 'Uninstall from Location'}
              </button>

              <ConfirmationDialog
                open={showUninstallConfirm}
                onOpenChange={setShowUninstallConfirm}
                onConfirm={handleUninstall}
                title="Confirm Uninstallation"
                description="Are you sure you want to uninstall InboxSentinel from this location? This will remove all access tokens."
                confirmText="Uninstall"
                cancelText="Cancel"
                variant="danger"
              />

              {uninstallMessage && (
                <div className={`mt-4 p-3 rounded-lg text-center ${
                  uninstallMessage.includes('✅')
                    ? 'bg-green-500/20 border border-green-500/50 text-green-100'
                    : 'bg-red-500/20 border border-red-500/50 text-red-100'
                }`}>
                  {uninstallMessage}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
